import { useEffect, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { LazyLoadImage } from "react-lazy-load-image-component"
import { makeAutoObservable } from "mobx"
import styles from "./styles.module.scss"
import classNames from "classnames"
import { useQuery } from "@tanstack/react-query"
import { DefaultSpinner } from "../../loaders/DefaultSpinner"

interface ErrorHandledImageProps {
  uri: string
  errorMessage?: string
  alt?: string
  className?: string
  onLoadingChange?: (value: "loading" | "loaded") => void
  onMouseMove?: (e: React.MouseEvent<HTMLImageElement>) => void
  onMouseLeave?: () => void
  enableCaching?: boolean
}

class ImageState {
  isError: boolean
  imageState: null | "loading" | "loaded"

  constructor() {
    this.isError = false
    this.imageState = null

    makeAutoObservable(this)
  }

  setIsError(value: boolean) {
    this.isError = value
  }

  setImageState(value: null | "loading" | "loaded") {
    this.imageState = value
  }
}

const fetchImage = async (url: string) => {
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    return URL.createObjectURL(blob)
  } catch {
    return ""
  }
}

export const ErrorHandledImage = observer(function ErrorHandledImage({
  uri,
  errorMessage,
  alt,
  className,
  onLoadingChange,
  onMouseMove,
  onMouseLeave,
  enableCaching = false,
}: ErrorHandledImageProps) {
  const state = useMemo(() => new ImageState(), [])

  const {
    data: imageUrl,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ["image", uri],
    queryFn: () => fetchImage(uri),
    staleTime: Infinity,
    retry: false,
    enabled: enableCaching,
  })

  useEffect(() => {
    if (state.imageState !== "loading") return () => undefined

    const timer = setTimeout(() => {
      if (state.imageState !== "loaded") {
        state.setIsError(true)
      }
    }, 10000)

    return () => clearTimeout(timer)
  }, [state.imageState])

  const handleChangeImageState = (value: "loading" | "loaded") => {
    if (onLoadingChange) onLoadingChange(value)
    state.setImageState(value)
  }

  return (
    <div className={styles.imageWrapper}>
      <LazyLoadImage
        src={enableCaching ? imageUrl : uri}
        alt={alt}
        onError={() => state.setIsError(true)}
        onLoadStart={() => handleChangeImageState("loading")}
        onLoad={() => handleChangeImageState("loaded")}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        className={classNames(className || undefined, state.isError && styles.imageError)}
      />
      {isLoading && (
        <div className={classNames(styles.imagePlaceholder, className || undefined)}>
          <DefaultSpinner />
        </div>
      )}
      {(state.isError || isError) && errorMessage && (
        <div className={classNames(styles.errorOverlay, className || undefined)}>
          <span className={styles.errorMessage}>{errorMessage}</span>
        </div>
      )}
    </div>
  )
})
